/**
 * @fileoverview Merge-field illustration: the {{ picker autocomplete in the editor (Phase 5, J3).
 * @module ui-admin/help/illustrations/IllustrationMergeField
 *
 * Responsibilities: - Faithfully recreate the rich-text editor area showing the merge-field
 *                     picker dropdown triggered by typing {{, with the field list as it appears.
 * Dependencies:     - React; shared illustration primitives.
 * Architecture:     - Presentation-only SVG. No state, no data, no side effects.
 * Notes:            - Mirrors the RichTextEditor merge-field autocomplete behaviour.
 */

import React from 'react';
import { CalloutBadge, IllustrationFigure, ILLUS } from './illustrationPrimitives';

/**
 * @component IllustrationMergeField
 * Purpose: Annotated diagram of the merge-field picker opened by typing {{ in the editor.
 */
export function IllustrationMergeField() {
  const fields = ['ownerName', 'propertyAddress', 'caseNumber', 'inspectionDate', 'inspectorName'];
  return (
    <IllustrationFigure
      title="The merge-field picker opened by typing two braces in the editor"
      viewBox="0 0 400 180"
      legend={[
        'Rich-text editor area where you type the document text.',
        'Typing {{ opens the merge-field picker at the cursor.',
        'Pick a field; it is inserted as a {{token}} that resolves at render time.',
      ]}
    >
      <rect x={2} y={2} width={396} height={176} rx={8} fill={ILLUS.white} stroke={ILLUS.gray200} />
      {/* editor toolbar */}
      <rect x={16} y={16} width={368} height={18} rx={4} fill={ILLUS.gray100} />
      {['B', 'I', 'U'].map((t, i) => (
        <text key={t} x={28 + i * 16} y={29} fontSize={9} fontWeight={700} fill={ILLUS.gray500}>{t}</text>
      ))}
      {/* editor body */}
      <rect x={16} y={38} width={368} height={126} rx={4} fill={ILLUS.white} stroke={ILLUS.gray200} />
      <text x={28} y={58} fontSize={9} fill={ILLUS.gray700}>Dear</text>
      {/* the typed {{ trigger */}
      <rect x={54} y={50} width={16} height={12} rx={2} fill={ILLUS.orange} opacity={0.15} />
      <text x={56} y={59} fontSize={9} fontWeight={700} fill={ILLUS.orangeDark}>{'{{'}</text>
      {/* picker dropdown */}
      <rect x={56} y={66} width={150} height={84} rx={5} fill={ILLUS.white} stroke={ILLUS.gray300} />
      {fields.map((f, i) => (
        <g key={f}>
          {i === 0 && <rect x={58} y={68 + i * 16} width={146} height={15} rx={3} fill={ILLUS.gray100} />}
          <text x={64} y={79 + i * 16} fontSize={8.5} fill={i === 0 ? ILLUS.orangeDark : ILLUS.gray700}>
            {`{{${f}}}`}
          </text>
        </g>
      ))}

      {/* Callouts */}
      <CalloutBadge n={1} x={372} y={48} />
      <CalloutBadge n={2} x={62} y={48} />
      <CalloutBadge n={3} x={196} y={76} />
    </IllustrationFigure>
  );
}
